import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import {
    getHeldActionLabel,
    getRetryAuditHint,
    getRunAuditHint,
    RETRY_AUDIT_EXPLANATION,
    UPLOAD_GATE_WRAPPER_CLASS,
} from '@client/components/compliance-audit/audit-action-hints';
import { ComplianceAuditEvidencePreviewModal } from '@client/components/compliance-audit/ComplianceAuditEvidencePreviewModal';
import { ComplianceAuditStepContextPanel } from '@client/components/compliance-audit/ComplianceAuditStepContextPanel';
import { ComplianceAuditStepDetail } from '@client/components/compliance-audit/ComplianceAuditStepDetail';
import { ComplianceAuditStepList } from '@client/components/compliance-audit/ComplianceAuditStepList';
import {
    COMPLIANCE_AUDIT_POLL_INTERVAL_MS,
    COMPLIANCE_AUDIT_TERMINAL_STEP_STATUSES,
    complianceAuditPollInterval,
} from '@client/components/compliance-audit/polling';
import { useComplianceAuditUploadingCount } from '@client/components/compliance-audit/upload-gate-store';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useIsUIDebugInfoEnabled } from '@client/hooks/system/useIsUIDebugInfoEnabled';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { downloadBlob } from '@client/utils/common/blob';
import { ComplianceAuditStatus, ComplianceAuditStepStatus } from '@shared/prisma/enums';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { BarChart3, Download, Play, RotateCw, StickyNoteOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// CUR-168 — Compliance audit detail page. Three-column layout (steps /
// step detail / context panel). Mirrors the products-v2 routing pattern
// (top-level segment, no nested tabs). Sub-components live in
// `@client/components/compliance-audit/` so the same modules can be reused
// across detail / edit surfaces.

export const Route = createFileRoute('/console/$orgId/tools/compliance-audit/details/$complianceAuditId')({
    staticData: {
        metadata: {
            title: 'Compliance audit',
        },
        presentation: 'fullPage',
        ui: {
            className: 'p-0',
            heightVariant: 'fit',
            // Three-column workspace: the columns size themselves against the
            // viewport, so the global `container mx-auto` layout preference would
            // squeeze them into an unusable centred strip. Pin full width and take
            // the toggle off the top bar rather than let it break the page.
            widthVariant: 'fullWidth',
            disableLayoutSwitch: true,
        },
    },
    params: {
        parse({ complianceAuditId }) {
            return { complianceAuditId: BigInt(complianceAuditId) };
        },
    },
    beforeLoad({ context: { trpcUtils }, params: { complianceAuditId } }): ShareableRouteContext {
        void trpcUtils.complianceAudit.getComplianceAudit.ensureData({ id: complianceAuditId });

        return {
            breadcrumb: {
                async title() {
                    const complianceAudit = await trpcUtils.complianceAudit.getComplianceAudit.ensureData({
                        id: complianceAuditId,
                    });

                    return complianceAudit.insuredName ?? `Compliance audit #${complianceAudit.id}`;
                },
            },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplianceAuditDetails />
        </PageController>
    );
}

function ComplianceAuditDetails() {
    const { orgId, complianceAuditId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    // Post-action force-poll window. The sustained "the engine owes the chat a
    // reply" polling is carried by the header's server-computed
    // `isAwaitingChatResponse` (see `complianceAuditPollInterval`); this window
    // remains for the transitions that flag cannot cover — the rerun of an ERROR
    // audit (ERROR is a non-polling status until the job flips it) — and as a
    // short belt-and-braces resume right after an answer.
    const [forcePollUntil, setForcePollUntil] = useState(0);
    // CUR-570 — the force-poll window is a wall-clock deadline. The child per-step
    // poll reads `isAuditProgressing` as a render-time snapshot, so if the header
    // data stops changing (e.g. a WAITING step never reclaimed) nothing re-renders
    // at the deadline and the child would keep its stale `true`. Force one render
    // exactly when the window closes so the gate recomputes to false.
    const [, forceRerenderTick] = useState(0);
    useEffect(() => {
        if (forcePollUntil <= Date.now()) return;
        const timer = setTimeout(() => forceRerenderTick((n) => n + 1), forcePollUntil - Date.now());
        return () => clearTimeout(timer);
    }, [forcePollUntil]);

    const {
        data: complianceAudit,
        isPending,
        error,
        refetch,
    } = trpc.complianceAudit.getComplianceAudit.useQuery(
        { id: complianceAuditId },
        {
            // Live progress (§5.3): once the run has started, poll ~2s until the
            // chain reaches a state where it is no longer actively progressing.
            // A WAITING audit keeps the cadence while the engine owes the open
            // conversation its reply (`isAwaitingChatResponse` on the payload) and
            // stops once the question is on screen and the turn is the auditor's.
            refetchInterval: (query) => {
                const audit = query.state.data;
                if (!audit?.startedAt) return false;
                // Post-answer / post-rerun window: the cached header still shows the
                // old non-polling status until the forced refetch lands.
                if (Date.now() < forcePollUntil) return COMPLIANCE_AUDIT_POLL_INTERVAL_MS;
                return complianceAuditPollInterval(audit);
            },
        },
    );
    const { formatDateTime } = useFormatter();
    // CUR-778 — the statistics overlay's gate. The statistics route's `beforeLoad`
    // evaluates the same predicate through `readIsUIDebugInfoEnabled()`, so the
    // button and the URL are hidden and blocked in exactly the same cases.
    const isDebugInfoEnabled = useIsUIDebugInfoEnabled();
    // CUR-706 — a file the library is still uploading is not yet a `File` row, so
    // the server cannot see it: a run started now would assess a corpus missing the
    // evidence the auditor is mid-way through attaching. The library publishes its
    // in-flight count here (uploads and unsettled attaches; failed rows excluded).
    // A client-side ergonomic guard only — the server's "at least one file" and
    // one-shot `startedAt` preconditions remain authoritative.
    const isUploadInProgress = useComplianceAuditUploadingCount(complianceAuditId) > 0;

    const runAudit = trpc.complianceAudit.runComplianceAudit.useMutation({
        onSuccess() {
            void refetch();
        },
        onError(mutationError) {
            errorMessage.show(mutationError);
        },
    });

    // Retry an audit halted on a step ERROR — resumes the chain from the errored
    // step. ERROR is a non-polling status, so force the short poll window (as on
    // answer) to pick the resumed run back up and flip the badge ERROR → Running.
    const rerunAudit = trpc.complianceAudit.rerunComplianceAudit.useMutation({
        onSuccess() {
            setForcePollUntil(Date.now() + 30_000);
            void refetch();
            // CUR-570 — the errored step's detail lives under `getComplianceAuditStep`
            // and ERROR is terminal, so its poll is stopped; refetching only the header
            // would leave the centre pane on the stale ERROR snapshot. Invalidate the
            // step + file caches so the resumed run repaints (the force window above
            // then keeps them live).
            void trpcUtils.complianceAudit.getComplianceAuditStep.invalidate();
            void trpcUtils.complianceAudit.listComplianceAuditFiles.invalidate({ complianceAuditId });
        },
        onError(mutationError) {
            errorMessage.show(mutationError);
        },
    });

    // Full-audit PDF export (§ Download): generated server-side and returned as
    // base64, converted to a Blob and downloaded via the anchor pattern. Always
    // available regardless of run status; failure surfaces a message and the
    // button re-enables (mutation isPending clears on settle).
    const exportPdf = trpc.complianceAudit.exportComplianceAuditPdf.useMutation({
        onSuccess({ base64, fileName }) {
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
            }
            downloadBlob(new Blob([bytes], { type: 'application/pdf' }), fileName);
        },
        onError(mutationError) {
            errorMessage.show(mutationError);
        },
    });

    // Undefined until the user picks a step; `selectedStep` falls back to the
    // first step, so the initial render highlights step 1 without dead state.
    const [selectedStepId, setSelectedStepId] = useState<bigint>();

    // Per-step run-start anchors for the live "Running Ns" timer. Owned here (the
    // page stays mounted across step navigation) so the timer survives switching
    // steps and back — the step detail's progress card unmounts on navigation,
    // which would otherwise restart the clock. Declared before the early returns
    // to keep hook order stable; populated below once the audit has loaded.
    const runStartAnchors = useRef<Map<string, Date>>(new Map());

    // CUR-570 — whether the chain is actively progressing, mirroring the audit poll's
    // own gate (incl. the post-answer force window). Gates the centre pane's per-step
    // poll so only a live audit drives per-step refetches. Computed before the early
    // returns so the terminal-transition effect below can be an unconditional hook.
    const isAuditProgressing = Date.now() < forcePollUntil || complianceAuditPollInterval(complianceAudit) !== false;

    // CUR-570 — the per-step and file-library polls are gated by `isAuditProgressing`,
    // derived from the independently-scheduled light header. The header can observe a
    // settled/terminal status one poll before the child queries fetch the engine's
    // final write, then this gate flips false and stops the child mid-flight — leaving
    // the open step stale on PENDING and a newly-cited file still shown as deletable
    // (the server delete has no evidence guard). On the true→false transition, force
    // one final refetch of the mounted step + file caches so the last tick lands.
    // Every step first selected under a closed gate is also reconciled once: its
    // fresh hover-prefetched cache may still say PENDING, while the closed header
    // gate disables both its mount refetch and interval. That refresh is
    // conditional — on a settled audit (the normal browsing case) the left rail
    // has just hover-prefetched the step, so invalidating unconditionally would
    // read the heavy body twice per selection and cancel out the prefetch. Only a
    // cached snapshot whose status, override and change version all agree with the
    // index is safe to reuse.
    const activeStep = complianceAudit?.Steps.find((step) => step.id === selectedStepId) ?? complianceAudit?.Steps[0];
    const activeStepId = activeStep?.id;
    const wasAuditProgressingRef = useRef<boolean | null>(null);
    const refreshedClosedGateStepIdRef = useRef<bigint | undefined>(undefined);
    // Per step, the index version this page has already reconciled — set by either
    // coordinator below so the two never issue a second heavy read for the same
    // header state.
    const reconciledStepVersionsRef = useRef<Map<string, number>>(new Map());
    useEffect(() => {
        // Keep the initial sentinel until the header has loaded. Otherwise a
        // pending first render would consume the settled-mount refresh.
        if (!complianceAudit) return;

        const wasAuditProgressing = wasAuditProgressingRef.current;
        if (!isAuditProgressing && activeStep !== undefined && refreshedClosedGateStepIdRef.current !== activeStep.id) {
            const cachedStep = trpcUtils.complianceAudit.getComplianceAuditStep.getData({
                complianceAuditId,
                stepId: activeStep.id,
            });
            const cacheAgreesWithIndex =
                cachedStep !== undefined &&
                COMPLIANCE_AUDIT_TERMINAL_STEP_STATUSES.includes(cachedStep.status) &&
                cachedStep.status === activeStep.status &&
                (cachedStep.overrideStatus ?? null) === (activeStep.overrideStatus ?? null) &&
                cachedStep.updatedAt.getTime() === activeStep.updatedAt.getTime();
            if (!cacheAgreesWithIndex) {
                void trpcUtils.complianceAudit.getComplianceAuditStep.invalidate({
                    complianceAuditId,
                    stepId: activeStep.id,
                });
            }
            refreshedClosedGateStepIdRef.current = activeStep.id;
            reconciledStepVersionsRef.current.set(activeStep.id.toString(), activeStep.updatedAt.getTime());
        }
        if (wasAuditProgressing === true && !isAuditProgressing) {
            void trpcUtils.complianceAudit.listComplianceAuditFiles.invalidate({ complianceAuditId });
        } else if (isAuditProgressing) {
            // A future settled transition must refresh whichever step is active,
            // even if it was the last one selected during an earlier closed gate.
            refreshedClosedGateStepIdRef.current = undefined;
        }
        wasAuditProgressingRef.current = isAuditProgressing;
    }, [activeStep, complianceAudit, complianceAuditId, isAuditProgressing, trpcUtils]);

    // CUR-570 — a remote edit that leaves a step terminal (another auditor overriding
    // PASSED → FAILED, a recommendation acknowledgement) changes only fields the centre
    // pane reads: the per-step poll sees two terminal statuses and stops, and the
    // closed-gate coordinator above never fires while the chain keeps progressing
    // (gate true → true). Both the light header and the lazy step read return the
    // step's `updatedAt`, so the comparison made here is between *this tab's cached
    // detail* and the index — never between two successive index reads. A cached
    // snapshot older than the index is stale by definition and is invalidated,
    // whichever writes opened the gap and whether or not this tab ever observed the
    // intermediate header states; a cache at or ahead of the index (its own read is
    // newer than the last header tick) is left alone. That keeps the millisecond
    // resolution of a JS `Date` from being load-bearing: two writes sharing a
    // millisecond can no longer hide each other, because the only equality that
    // suppresses a refresh is "the row I read is the row the index is describing".
    // A step the centre pane is already polling is skipped — its interval refetch
    // carries the same update.
    const pendingLocalStepRefreshRef = useRef<Map<string, number>>(new Map());
    useEffect(() => {
        if (!complianceAudit) return;
        for (const step of complianceAudit.Steps) {
            const key = step.id.toString();
            const indexVersion = step.updatedAt.getTime();
            if (reconciledStepVersionsRef.current.get(key) === indexVersion) continue;

            const cachedVersion = trpcUtils.complianceAudit.getComplianceAuditStep
                .getData({ complianceAuditId, stepId: step.id })
                ?.updatedAt.getTime();

            // A local override/acknowledgement already owns the detail refresh, and its
            // read necessarily returns everything committed before it — including a
            // remote write racing it in the same millisecond. Wait for that read to land
            // (the mutation's own post-write version, or newer) instead of issuing a
            // second heavy request; from then on the index is compared against the
            // refreshed cache like any other change, so nothing remote is swallowed.
            const pendingLocalVersion = pendingLocalStepRefreshRef.current.get(key);
            if (pendingLocalVersion !== undefined) {
                if (cachedVersion === undefined || cachedVersion < pendingLocalVersion) continue;
                pendingLocalStepRefreshRef.current.delete(key);
            }

            // Nothing cached: there is no snapshot to invalidate and react-query fetches
            // on mount. Skip without recording, so a cache arriving later is compared.
            if (cachedVersion === undefined) continue;
            const isLivePolled =
                step.id === activeStepId &&
                isAuditProgressing &&
                !COMPLIANCE_AUDIT_TERMINAL_STEP_STATUSES.includes(step.status);
            if (isLivePolled) continue;

            reconciledStepVersionsRef.current.set(key, indexVersion);
            if (indexVersion <= cachedVersion) continue;
            void trpcUtils.complianceAudit.getComplianceAuditStep.invalidate({ complianceAuditId, stepId: step.id });
        }
    }, [activeStepId, complianceAudit, complianceAuditId, isAuditProgressing, trpcUtils]);

    // Surface query failures (invalid id, revoked flag/permission → NOT_FOUND) instead of an
    // infinite spinner — `isPending` is false on error, so this must precede the loading guard.
    if (error) {
        return <InlineQueryError error={error} onRetry={refetch} />;
    }
    if (isPending || !complianceAudit) {
        return <Loading />;
    }

    const selectedStep = complianceAudit.Steps.find((step) => step.id === selectedStepId) ?? complianceAudit.Steps[0];

    // CUR-706 — the reason each held action carries, or '' when it needs none (see
    // `audit-action-hints.ts` for which reason wins and why a started audit is silent).
    const runAuditHint = getRunAuditHint({
        hasStarted: !!complianceAudit.startedAt,
        isUploadInProgress,
        fileCount: complianceAudit.Files.length,
    });
    const retryAuditHint = getRetryAuditHint({ isUploadInProgress });

    // Maintain the run-start anchors: set when the client first observes a step
    // PENDING, cleared once it leaves PENDING, so a finish or re-run starts a
    // fresh timer.
    for (const step of complianceAudit.Steps) {
        const key = step.id.toString();
        if (step.status === ComplianceAuditStepStatus.PENDING) {
            if (!runStartAnchors.current.has(key)) runStartAnchors.current.set(key, new Date());
        } else {
            runStartAnchors.current.delete(key);
        }
    }
    const selectedStepRunStartedAt = runStartAnchors.current.get(selectedStep.id.toString());

    // While any step is running the chain is actively processing — lock the chat
    // composer so the auditor can't send an answer mid-run.
    const isAuditRunning = complianceAudit.Steps.some((step) => step.status === ComplianceAuditStepStatus.PENDING);

    // CUR-551 — assessed vs not-applicable summary, derived from the persisted step
    // statuses. Surfaced beside the status badge only when at least one step was
    // routed out, so a file where a whole pillar was skipped can't read as a
    // fully-reviewed pass. A NOT_APPLICABLE step is resolved but never assessed.
    const totalSteps = complianceAudit.Steps.length;
    const notApplicableSteps = complianceAudit.Steps.filter(
        (step) => step.status === ComplianceAuditStepStatus.NOT_APPLICABLE,
    ).length;
    const assessedSteps = complianceAudit.Steps.filter(
        (step) =>
            step.status === ComplianceAuditStepStatus.PASSED ||
            step.status === ComplianceAuditStepStatus.FAILED ||
            step.status === ComplianceAuditStepStatus.ERROR,
    ).length;
    // CUR-551 — every step was routed out (all NOT_APPLICABLE): resolved, but never
    // substantively reviewed. A quiet icon Tag beside the status flags it at a glance.
    const noStepAssessed = totalSteps > 0 && notApplicableSteps === totalSteps;

    return (
        <div className="flex h-full flex-col">
            <div className="border-separator border-b bg-card px-7 py-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h1 className="m-0 font-semibold text-foreground text-xl">
                            {complianceAudit.insuredName ?? '—'}{' '}
                            <span className="font-normal text-muted-foreground">
                                · {complianceAudit.policyNumber ?? 'No policy number'}
                            </span>
                        </h1>
                        <div className="mt-1.5 flex flex-wrap items-center gap-3 text-foreground text-xs">
                            <div className="flex items-center gap-1">
                                <StatusBadge
                                    status={complianceAudit.status}
                                    module="complianceAudit"
                                    variant="outlined"
                                />
                                {noStepAssessed && (
                                    <Tooltip title="No steps assessed — every step was not applicable">
                                        <Tag
                                            variant="outlined"
                                            className="m-0 inline-flex h-5.5 items-center"
                                            icon={<StickyNoteOff className="size-3.5" />}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            {notApplicableSteps > 0 && (
                                <span className="font-medium text-subtle-foreground">
                                    {assessedSteps} of {totalSteps} steps assessed · {notApplicableSteps} not applicable
                                </span>
                            )}
                            <span>
                                <span className="text-subtle-foreground">Created </span>
                                {formatDateTime(complianceAudit.createdAt)}
                            </span>
                            {complianceAudit.Owner && (
                                <span className="flex items-center gap-1">
                                    <span className="text-subtle-foreground">Auditor </span>
                                    <User
                                        userId={complianceAudit.Owner.userId}
                                        name={complianceAudit.Owner.fullName}
                                        photoUrl={complianceAudit.Owner.User?.photoUrl}
                                        size="small"
                                    />
                                </span>
                            )}
                            {complianceAudit.brokerName && (
                                <span>
                                    <span className="text-subtle-foreground">Broker </span>
                                    {complianceAudit.brokerName}
                                </span>
                            )}
                            {complianceAudit.authorisedRepresentative && (
                                <span>
                                    <span className="text-subtle-foreground">AR </span>
                                    {complianceAudit.authorisedRepresentative}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex shrink-0 items-end gap-2 self-end">
                        {/* CUR-168 — "Retry audit" (§4.4): only when the chain has halted on a
                            step ERROR. Resumes from the errored step; "Run audit" stays disabled
                            (startedAt is set). */}
                        {complianceAudit.status === ComplianceAuditStatus.ERROR && (
                            <Tooltip trigger={['hover', 'focus']} title={retryAuditHint || RETRY_AUDIT_EXPLANATION}>
                                {/* CUR-706 — see UPLOAD_GATE_WRAPPER_CLASS: the wrapper carries
                                    the tooltip while the gate holds the button disabled. */}
                                <span
                                    className={UPLOAD_GATE_WRAPPER_CLASS}
                                    tabIndex={retryAuditHint ? 0 : undefined}
                                    aria-label={getHeldActionLabel('Retry audit', retryAuditHint)}
                                >
                                    <Button
                                        type="primary"
                                        icon={<RotateCw size={14} />}
                                        loading={rerunAudit.isPending}
                                        disabled={rerunAudit.isPending || isUploadInProgress}
                                        onClick={() => rerunAudit.mutate({ complianceAuditId: complianceAudit.id })}
                                    >
                                        Retry audit
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {/* Opens the statistics overlay page (tokens, cost, files, steps, timing,
                            logs). CUR-778 — gated by the UI debug-info surface (debug-panel toggle
                            plus `enable-debug-info`) rather than the build mode, so a user who has
                            deliberately turned debug info on reaches it in deployed environments too.
                            Order (design §): Statistics → Download → Run audit. */}
                        {isDebugInfoEnabled && (
                            <Button.Icon.Link
                                icon={<BarChart3 />}
                                bordered
                                tooltip="Audit statistics"
                                aria-label="Audit statistics"
                                to="/console/$orgId/tools/compliance-audit/details/$complianceAuditId/statistics"
                                params={{ orgId, complianceAuditId: complianceAudit.id }}
                            />
                        )}
                        {/* Full-audit PDF export — always available, independent of run status.
                            A not-yet-run audit exports its header + files with an "Audit not yet run" note. */}
                        <Button.Icon
                            icon={<Download />}
                            bordered
                            tooltip="Download audit PDF"
                            aria-label="Download audit PDF"
                            loading={exportPdf.isPending}
                            onClick={() => exportPdf.mutate({ complianceAuditId: complianceAudit.id })}
                        />
                        {/* CUR-168 — one-shot "Run audit" trigger (§5.1): enabled only when
                            files are attached and the audit has never been run. CUR-706 adds
                            the library's in-flight uploads to that condition. */}
                        <Tooltip trigger={['hover', 'focus']} title={runAuditHint}>
                            {/* See the retry button above: the hint has to hang off a wrapper to
                                survive the disabled state it explains. Both held states carry a
                                reason, so the wrapper is a focus target for either. */}
                            <span
                                className={UPLOAD_GATE_WRAPPER_CLASS}
                                tabIndex={runAuditHint ? 0 : undefined}
                                aria-label={getHeldActionLabel('Run audit', runAuditHint)}
                            >
                                <Button
                                    type="primary"
                                    icon={<Play size={14} />}
                                    disabled={
                                        complianceAudit.Files.length === 0 ||
                                        !!complianceAudit.startedAt ||
                                        runAudit.isPending ||
                                        isUploadInProgress
                                    }
                                    loading={runAudit.isPending}
                                    onClick={() => runAudit.mutate({ complianceAuditId: complianceAudit.id })}
                                >
                                    Run audit
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div className="flex min-h-0 flex-1">
                <div className="w-65 shrink-0 border-separator border-r bg-card">
                    <ComplianceAuditStepList
                        steps={complianceAudit.Steps}
                        complianceAuditId={complianceAudit.id}
                        selectedStepId={selectedStep.id}
                        onSelect={setSelectedStepId}
                    />
                </div>
                <div className="scrollbar-none min-w-0 flex-1 overflow-auto">
                    <ComplianceAuditStepDetail
                        stepId={selectedStep.id}
                        complianceAuditId={complianceAudit.id}
                        indexedStatus={selectedStep.status}
                        files={complianceAudit.Files}
                        runStartedAt={selectedStepRunStartedAt}
                        isAuditRunning={isAuditRunning}
                        isAuditProgressing={isAuditProgressing}
                        auditStatus={complianceAudit.status}
                        onStepMutated={(stepId, updatedAt) => {
                            pendingLocalStepRefreshRef.current.set(stepId.toString(), updatedAt.getTime());
                        }}
                        // Belt-and-braces resume: the composer's own header invalidation
                        // brings back `isAwaitingChatResponse: true`, which sustains the
                        // poll until the engine replies; the short window only bridges the
                        // gap while that refetch is in flight.
                        onAnswered={() => setForcePollUntil(Date.now() + 30_000)}
                    />
                </div>
                <div className="w-100 shrink-0 border-separator border-l bg-card">
                    <ComplianceAuditStepContextPanel
                        complianceAuditId={complianceAudit.id}
                        step={selectedStep}
                        files={complianceAudit.Files}
                        isAuditProgressing={isAuditProgressing}
                    />
                </div>
            </div>

            {/* CUR-1068 — one viewer instance serves every evidence link on the page
                (library rows, citations, note and chat attachments). Keyed by audit, like the
                library above: the route component is reused across audit ids, and a remount is
                what clears a viewer left open on the previous audit. */}
            <ComplianceAuditEvidencePreviewModal key={complianceAudit.id.toString()} />

            {/* Renders the statistics overlay child route on top while the detail page stays visible. */}
            <Outlet />
        </div>
    );
}
